/** One material in its type's table: title, audience, what it is, and the actions the caller may take. */

import { Download, ExternalLink, Eye, FileText, Link2, Pencil, RotateCcw, Archive, Trash2 } from 'lucide-react';

import { materialFileUrl } from '@/api/queries/materials';
import type { MaterialOut } from '@/api/types/materials';
import { formatBytes } from '@/ui/Attachments';
import { RowMenu, type RowMenuItem } from '@/ui/RowMenu';

import { AudienceBadge } from '../cases/badges';
import { linkHost, safeHref } from './materialList';

export type MaterialAction = 'edit' | 'supersede' | 'restore' | 'remove' | 'preview';

function What({ material }: { material: MaterialOut }) {
  if (material.kind === 'link') {
    return (
      <span className="hint">
        <Link2 aria-hidden="true" className="inline-icon" /> {linkHost(material.url)}
      </span>
    );
  }
  if (!material.file) return <span className="hint">No file</span>;
  return (
    <span className="hint">
      <FileText aria-hidden="true" className="inline-icon" /> {material.file.filename}, {formatBytes(material.file.size_bytes)}
    </span>
  );
}

function ReaderActions({ assetId, material, onAction }: { assetId: string; material: MaterialOut; onAction: (a: MaterialAction) => void }) {
  if (material.kind === 'link') {
    const href = safeHref(material.url);
    if (!href) return null;
    return (
      <a className="btn btn-secondary btn-sm" href={href} target="_blank" rel="noopener noreferrer" aria-label={`Open ${material.title} (opens in a new tab)`}>
        <ExternalLink aria-hidden="true" /> Open link
      </a>
    );
  }
  const url = materialFileUrl(assetId, material.id);
  return (
    <>
      {material.preview === 'pdf' && (
        <a className="btn btn-secondary btn-sm" href={url} target="_blank" rel="noopener noreferrer" aria-label={`Preview ${material.title} (opens in a new tab)`}>
          <Eye aria-hidden="true" /> Preview
        </a>
      )}
      {material.preview === 'markdown' && (
        <button type="button" className="btn btn-secondary btn-sm" aria-label={`Preview ${material.title}`} onClick={() => onAction('preview')}>
          <Eye aria-hidden="true" /> Preview
        </button>
      )}
      <a className="btn btn-secondary btn-sm" href={url} download={material.file?.filename ?? true} aria-label={`Download ${material.title}`}>
        <Download aria-hidden="true" /> Download
      </a>
    </>
  );
}

function managerItems(material: MaterialOut, onAction: (a: MaterialAction) => void): RowMenuItem[] {
  return [
    { label: 'Edit', icon: <Pencil aria-hidden="true" />, onSelect: () => onAction('edit') },
    material.superseded_by
      ? { label: 'Make current again', icon: <RotateCcw aria-hidden="true" />, onSelect: () => onAction('restore') }
      : { label: 'Mark superseded', icon: <Archive aria-hidden="true" />, onSelect: () => onAction('supersede') },
    { label: 'Remove', icon: <Trash2 aria-hidden="true" />, onSelect: () => onAction('remove') },
  ];
}

export function MaterialRow({
  assetId,
  material,
  manage,
  onAction,
}: {
  assetId: string;
  material: MaterialOut;
  manage: boolean;
  onAction: (action: MaterialAction) => void;
}) {
  return (
    <tr>
      <th scope="row">{material.title}</th>
      <td>
        <AudienceBadge audience={material.audience} />
      </td>
      <td>
        <What material={material} />
      </td>
      <td>
        <div className="row-actions">
          <ReaderActions assetId={assetId} material={material} onAction={onAction} />
          {manage && <RowMenu label={`Actions for ${material.title}`} items={managerItems(material, onAction)} />}
        </div>
      </td>
    </tr>
  );
}
